import { toast } from 'sonner';

import { useAuth } from 'src/context/auth/use-auth';
import { useForgotPasswordSlice, StepEnum } from 'src/slices';
import {
  ForgotPasswordForm,
  IForgotPasswordForm,
  ResetPasswordConfirmation,
  IResetPasswordForm,
  ResetPasswordForm,
} from 'src/components/Authentication';
import { AuthHeading } from 'src/components/Layout';

const ForgotPasswordContainer = () => {
  const { sendPasswordResetEmail, confirmPasswordReset } = useAuth();
  const { state, sendCode, reset } = useForgotPasswordSlice();
  const { email, activeStep } = state;

  const onRequestCodeFormFinish = async ({ email }: IForgotPasswordForm) => {
    try {
      await sendPasswordResetEmail(email);
      sendCode(email);
    } catch (e: unknown) {
      if (e instanceof Error) toast.error(e.message || 'Something went wrong');
    }
  };

  const onConfirmationFormFinish = async ({ code, password }: Omit<IResetPasswordForm, 'confirmPassword'>) => {
    try {
      await confirmPasswordReset(email, code, password);
      reset();
    } catch (e: unknown) {
      if (e instanceof Error) toast.error(e.message || 'Something went wrong');
    }
  };

  const stepsDataEnum: Record<
    StepEnum.INITIAL | StepEnum.RESET | StepEnum.CONFIRMATION,
    { title: string; description: string; render: () => React.ReactNode }
  > = {
    [StepEnum.INITIAL]: {
      title: 'Reset your password',
      description: "Enter your email address and we'll send you a reset code.",
      render: () => <ForgotPasswordForm onSubmit={onRequestCodeFormFinish} />,
    },
    [StepEnum.RESET]: {
      title: 'Create your new password',
      description: 'Enter the reset code sent to your email and create a new password.',
      render: () => <ResetPasswordForm onSubmit={onConfirmationFormFinish} email={email} />,
    },
    [StepEnum.CONFIRMATION]: {
      title: 'Password updated',
      description: 'Your password has been updated. You can now sign in with your new password.',
      render: () => <ResetPasswordConfirmation />,
    },
  };

  const step = activeStep in stepsDataEnum ? stepsDataEnum[activeStep as keyof typeof stepsDataEnum] : null;

  return (
    <>
      <AuthHeading title={step?.title || ''} description={step?.description || ''} />
      {step?.render()}
    </>
  );
};

export default ForgotPasswordContainer;
